import { useState } from "react";
import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingTable, NotAvailableState } from "@/components/ui/system-state";
import { EmptyState } from "@/components/ui/feedback";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { HotWalletTabs } from "@/features/hot-wallets/hot-wallet-tabs";
import { useHotWalletDeposits } from "@/hooks/use-hot-wallet-deposits";
import { useHotWalletsLocale } from "@/i18n/use-hot-wallets-locale";

/**
 * HW-S2: addresses & deposits explorer (SPEC-HW-8, WORKFLOW.md §3).
 * `/hot-wallets/:walletId/deposits`. Wired to GET .../deposits (SPEC-HW-8
 * §2), which does not exist behind the gateway yet — see dashboard.tsx for
 * why this page still attempts the real call.
 */
export default function HotWalletDepositsPage() {
  const { walletId = "" } = useParams<{ walletId: string }>();
  const { ui, dir } = useHotWalletsLocale();
  const [query, setQuery] = useState("");
  const { deposits, loading, error, refresh } = useHotWalletDeposits(walletId);

  const rows = deposits.filter((d) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return `${d.externalCustomerId ?? ""} ${d.address ?? ""} ${d.depositId}`.toLowerCase().includes(q);
  });

  return (
    <div dir={dir}>
      <PageHeader title={ui["deposits.title"]} subtitle={ui["deposits.subtitle"]} />
      <HotWalletTabs walletId={walletId} active="deposits" labels={ui} />

      <div className="mb-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={ui["deposits.searchPlaceholder"]}
          aria-label={ui["deposits.searchPlaceholder"]}
          className="w-full max-w-md rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary-soft"
        />
      </div>

      {loading && <LoadingTable rows={4} cols={5} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={ui["deposits.unavailableTitle"]}
            description={ui["deposits.unavailableDescription"]}
            reason={error.reasonCode}
          />
          <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          <button
            type="button"
            onClick={refresh}
            className="inline-flex min-h-[36px] items-center text-sm font-medium text-primary underline"
          >
            {ui["common.retry"]}
          </button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <EmptyState card title={ui["deposits.emptyTitle"]} description={ui["deposits.emptyDescription"]} />
      )}

      {!loading && !error && rows.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {rows.map((d) => (
            <li key={d.depositId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3 text-sm">
              {/* A missing field renders an explicit dash, never a blank
                  cell: an empty amount reads as zero to a person, and a
                  blank status reads as "nothing to report". Same honest-dash
                  convention as the sibling hot-wallets pages
                  (dashboard.tsx, reconciliation.tsx). */}
              {/* Three values in one row overflow 375px: a hex deposit address
                  has no break opportunities, so it gets its own full-width
                  line on a phone (`w-full` … `sm:w-auto`) and breaks rather
                  than widening the page. From sm it is back beside the amount
                  and status, which keep their width (`shrink-0`). */}
              <span className="w-full break-all font-mono text-xs text-muted sm:w-auto sm:min-w-0 sm:flex-1">
                {d.address ?? "—"}
              </span>
              <span className="shrink-0 font-semibold text-ink">
                {d.amount ?? "—"} {d.asset ?? "—"}
              </span>
              <span className="shrink-0 text-muted">{d.status ?? "—"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
